import { GRAPH_DEFAULTS, SCALES } from "../Enums/index.js";
import { formatNumberForScale } from "./scaleNumberFormatter.js";
import DocumentDO = App.Data.Explorer.DocumentDO;
import DocumentVerticalDTO = App.Data.DocumentVerticalDTO;

// Return suggested max being the highest possible stack.
// This is necessary for capacity. This axis is only looking at the regular data.
// If that is low, but the capacity high, the ticks would be incorrect.

const getHighestValue = (dataObj: Record<string, number[]>) =>
	Object.values(dataObj).reduce(
		(max, values) => Math.max(max, ...values),
		-Infinity
	);

const getLowestValue = (dataObj: Record<string, number[]>) =>
	Object.values(dataObj).reduce(
		(min, values) => Math.min(min, ...values),
		Infinity
	);

const synchronisedMax = (chart) => {
	const yMax = getHighestValue(chart.chart.$totalizer.stacktotal[SCALES.Y]);
	const ySecondaryMax = getHighestValue(
		chart.chart.$totalizer.stacktotal[SCALES.Y_SECONDARY]
	);
	return Math.max(0, yMax, ySecondaryMax);
};

const synchronisedMin = (chart) => {
	const yMin = getLowestValue(chart.chart.$totalizer.stacktotal[SCALES.Y]);
	const ySecondaryMin = getLowestValue(
		chart.chart.$totalizer.stacktotal[SCALES.Y_SECONDARY]
	);
	return Math.min(0, yMin, ySecondaryMin);
};

const hasSynchronisedYAxis = (derivedDocument: DocumentDO): boolean =>
	derivedDocument.is_y_axis_synchronised &&
	!!derivedDocument.secondary_vertical;

/**
 * Check if the vertical is of type mirrored.
 * If the verticals are synced, the other vertical being mirrored is also fine
 * Apparently doesn't need to work if the delta is enabled
 */
const hasMirroredYAxis = (
	derivedDocument: DocumentDO,
	vertical: DocumentVerticalDTO,
	isSynced: boolean
): boolean => {
	const isDeltaEnabled =
		derivedDocument.date_comparison?.isActive &&
		derivedDocument.date_comparison?.showDeltaAs == "primary";

	if (isDeltaEnabled) return false;

	const isVerticalMirrored = vertical.type === "mirrored";
	const isSyncedVerticalMirrored =
		isSynced &&
		(derivedDocument.vertical.type === "mirrored" ||
			derivedDocument?.secondary_vertical.type === "mirrored");

	return isVerticalMirrored || isSyncedVerticalMirrored;
};

const hasBoldZeroLine = (context): boolean => {
	const { min, max } = context.chart.scales.y;
	const zeroVisible = min < 0 && max > 0;

	return zeroVisible && context.tick.value === 0;
};

export const setTitle = (
	title: string,
	derivedDocument: DocumentDO,
	scale: "primary" | "secondary"
): string => {
	if (derivedDocument.date_comparison.showDeltaAs === scale) {
		const delta = derivedDocument.date_comparison.isDeltaAbsolute
			? "Absolute"
			: "%";
		return `∆ ${title} (${delta})`;
	}
	return title;
};

export const yScale = (
	derivedDocument: DocumentDO,
	title: string,
	isDarkMode: boolean,
	isSecondary: boolean = false
) => {
	const stacktotalKey = isSecondary ? SCALES.Y_SECONDARY : SCALES.Y;

	const suggestedLimitsMargin = derivedDocument.is_total_label_visible
		? 1.1
		: 1;

	const vertical = isSecondary
		? derivedDocument.secondary_vertical!
		: derivedDocument.vertical;

	const isSecondaryVerticalDeltaPercentage =
		derivedDocument.date_comparison.isActive &&
		!derivedDocument.date_comparison.isDeltaAbsolute &&
		derivedDocument.date_comparison.showDeltaAs === "secondary";

	return {
		stacked: () => {
			return !(derivedDocument.graph_type === "line");
		},
		beginAtZero: true,
		position: isSecondary ? "right" : undefined,
		title: {
			display: true,
			text: setTitle(
				title,
				derivedDocument,
				isSecondary ? "secondary" : "primary"
			),
			font: {
				size: 13,
			},
			color: isDarkMode
				? GRAPH_DEFAULTS.TEXT_COLOR_DARK
				: GRAPH_DEFAULTS.TEXT_COLOR,
		},
		grid: isSecondary
			? {
					drawOnChartArea: false,
			  }
			: {
					lineWidth: (context) => {
						return hasBoldZeroLine(context) ? 2 : 1;
					},
					tickColor: (context) => {
						return hasBoldZeroLine(context)
							? GRAPH_DEFAULTS.LINE_COLOR_THICK
							: GRAPH_DEFAULTS.LINE_COLOR;
					},
					color: (context) => {
						return hasBoldZeroLine(context)
							? GRAPH_DEFAULTS.LINE_COLOR_THICK
							: GRAPH_DEFAULTS.LINE_COLOR;
					},
			  },
		ticks: {
			font: {
				size: 12,
			},
			color: isDarkMode
				? GRAPH_DEFAULTS.TEXT_COLOR_DARK
				: GRAPH_DEFAULTS.TEXT_COLOR,
			maxTicksLimit: (chart) => {
				if (
					derivedDocument.formatForGraph === "percentage" ||
					isSecondaryVerticalDeltaPercentage
				) {
					return 12;
				}

				const maxValue = Math.abs(
					synchronisedMax(chart) - synchronisedMin(chart)
				);

				return Math.max(2, Math.min(12, maxValue + 1));
			},
			padding: 20,
			callback: (value, index, ticks) => {
				if (!isSecondary && derivedDocument.is_visualised_as_percentage_share) {
					return `${value}%`;
				}

				const middle = Math.floor(ticks.length / 2);
				const tickSpacing =
					ticks[middle + 1]?.value - ticks[middle]?.value || 0;

				if (!isSecondary) {
					// Primary vertical format can be changed by settings.
					const labelForCurrentTick = formatNumberForScale(
						derivedDocument.formatForGraph ?? vertical.format,
						value,
						tickSpacing
					);

					// We apply rounding after chartjs creates the ticks.
					// The can lead to multiple ticks being formatted in the same way.
					// To prevent this, we hide any tick which is the same as the next tick (such that we only show the last tick with that value)
					let labelForNextTick = null;
					if (index < ticks.length - 1) {
						labelForNextTick = formatNumberForScale(
							derivedDocument.formatForGraph ?? vertical.format,
							ticks[index + 1].value,
							tickSpacing
						);
					}

					if (labelForCurrentTick === labelForNextTick) {
						return "";
					}

					return labelForCurrentTick;
				}

				return formatNumberForScale(
					isSecondaryVerticalDeltaPercentage ? "percentage" : vertical.format,
					value,
					tickSpacing
				);
			},
		},
		min() {
			if (!derivedDocument.is_custom_boundaries_enabled) {
				return undefined;
			}
			return derivedDocument.custom_boundary_min;
		},
		max() {
			if (!derivedDocument.is_custom_boundaries_enabled) {
				return undefined;
			}
			return derivedDocument.custom_boundary_max;
		},
		suggestedMin(chart) {
			const isSynced = hasSynchronisedYAxis(derivedDocument);
			const isMirrored = hasMirroredYAxis(derivedDocument, vertical, isSynced);

			if (isSynced && isMirrored) {
				return (
					Math.min(synchronisedMin(chart), -synchronisedMax(chart)) *
					suggestedLimitsMargin
				);
			}
			if (isSynced) {
				return synchronisedMin(chart) * suggestedLimitsMargin;
			}
			if (isMirrored) {
				return (
					Math.min(
						getLowestValue(chart.chart.$totalizer.stacktotal[stacktotalKey]),
						-getHighestValue(chart.chart.$totalizer.stacktotal[stacktotalKey])
					) * suggestedLimitsMargin
				);
			}

			return undefined;
		},
		suggestedMax(chart) {
			// When using percentage share we usually want to keep the line at 100/110% but in some cases we have more than
			// 100%. In that case we will use the actual max value.
			// secondary can't be percentage share for now
			if (!isSecondary && derivedDocument.is_visualised_as_percentage_share) {
				return derivedDocument.graph_type === "line" ? 110 : 100;
			}

			const isSynced = hasSynchronisedYAxis(derivedDocument);
			const isMirrored = hasMirroredYAxis(derivedDocument, vertical, isSynced);


			if (isSynced && isMirrored) {
				return (
					Math.max(-synchronisedMin(chart), synchronisedMax(chart)) *
					suggestedLimitsMargin
				);
			}
			if (isSynced) {
				return synchronisedMax(chart) * suggestedLimitsMargin;
			}
			if (isMirrored) {
				return (
					Math.max(
						-getLowestValue(chart.chart.$totalizer.stacktotal[stacktotalKey]),
						getHighestValue(chart.chart.$totalizer.stacktotal[stacktotalKey])
					) * suggestedLimitsMargin
				);
			}

			return (
				getHighestValue(chart.chart.$totalizer.stacktotal[stacktotalKey]) *
				suggestedLimitsMargin
			);
		},
		afterBuildTicks(axis) {
			if (isSecondary) {
				return;
			}

			for (const scaleId of ["yMax", "yMin", "yCap"] as const) {
				const scale = axis.chart.scales[scaleId];
				if (!scale) continue;

				scale.options.min = axis.min;
				scale.options.max = axis.max;
				scale.options.suggestedMin = axis.suggestedMin;
				scale.options.suggestedMax = axis.suggestedMax;

				// Also set the computed values directly so pixel positioning is
				// correct in the current render cycle (not just the next one).
				scale.min = axis.min;
				scale.max = axis.max;
			}
		},
	};
};
